import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowUp } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const Footer = () => {
  const { siteName, logoUrl, supportEmail } = useSiteSettings();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderLink = (link) => (
    link.external ? (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-400 hover:text-white transition-colors text-sm"
      >
        {link.label}
      </a>
    ) : (
      <Link
        to={link.href}
        className="text-gray-400 hover:text-white transition-colors text-sm"
      >
        {link.label}
      </Link>
    )
  );

  const footerLinks = {
    products: [
      { label: 'Components', href: '/shop/products?category=components' },
      { label: 'Modules', href: '/shop/products?category=modules' },
      { label: 'Plugins', href: '/shop/products?category=plugins' },
      { label: 'Templates', href: '/shop/products?category=templates' },
      { label: 'Shop All', href: '/shop' },
      { label: 'Docs', href: '/docs' },
    ],
    programs: [
      { label: 'Custom Extensions', href: '/custom-extensions' },
      { label: 'Co-Marketing & Royalties', href: '/custom-extensions' },
      { label: 'Roadmap', href: '/roadmap' },
      { label: 'Suggest an Extension', href: '/suggest-extension' },
      { label: 'Live Demos', href: 'https://demo.aijoomla.org', external: true },
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Careers', href: '/careers' },
    ],
    support: [
      { label: 'My Account', href: '/account' },
      { label: 'License & Updates', href: '/account' },
      { label: 'Support', href: '/support' },
      { label: 'Technical Support', href: '/contact' },
      { label: 'Live Demos', href: 'https://demo.aijoomla.org', external: true },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms & Conditions', href: '/terms-conditions' },
      { label: 'Refund Policy', href: '/shipping-returns' },
      { label: 'Accessibility', href: '/accessibility' },
    ],
  };

  return (
    <footer className="bg-bots-darker border-t border-gray-800" data-testid="main-footer">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              {logoUrl ? (
                <img src={logoUrl} alt={siteName || 'AI Joomla'} className="h-10" />
              ) : (
                <span className="text-2xl font-bold text-white">{siteName || 'AI Joomla'}</span>
              )}
            </Link>
            <p className="text-gray-400 text-sm mb-4">
              Premium Joomla components, modules, plugins &amp; templates — engineered in-house for Joomla 4, 5 &amp; 6, with instant downloads and automatic updates.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2">
              <a 
                href="tel:8889900440" 
                className="flex items-center text-gray-400 hover:text-white transition-colors text-sm"
              >
                <Phone className="w-4 h-4 mr-2" />
                (888) 990-0440
              </a>
              <a 
                href={`mailto:${supportEmail || 'info@aijoomla.org'}`}
                className="flex items-center text-gray-400 hover:text-white transition-colors text-sm"
              >
                <Mail className="w-4 h-4 mr-2" />
                {supportEmail || 'info@aijoomla.org'}
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-semibold mb-4">Products</h4>
            <ul className="space-y-2">
              {footerLinks.products.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-white font-semibold mb-4">Programs</h4>
            <ul className="space-y-2">
              {footerLinks.programs.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-semibold mb-4">Support</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              © {currentYear} {siteName || 'AI Joomla'}. All rights reserved.
            </p>
            
            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              className="mt-4 md:mt-0 flex items-center text-gray-400 hover:text-white transition-colors text-sm"
              data-testid="back-to-top"
            >
              Back to top
              <ArrowUp className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
